import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosInstance } from '../routes/axiosInstance';

export const fetchPaneSeries = createAsyncThunk(
  'performancePane/fetchSeries',
  async ({ resourceId, track, horizonOffset, startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/market/forecaster-leaderboard/series', {
        params: {
          resource: resourceId,
          track,
          grain: 'day',
          start_date: startDate,
          end_date: endDate,
          horizon_offset: horizonOffset,
        },
      });
      return res.data?.data || res.data;
    } catch (err) {
      if (err?.response?.status === 403) return rejectWithValue({ forbidden: true });
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch performance series');
    }
  }
);

// Per-resource available horizons. The pane is cross-market, so its horizon
// switcher must offer the union across ALL resources, not the horizons of the
// resource currently selected on the dashboard (a D+1-only selection would
// otherwise hide every other market's horizons).
export const fetchPaneHorizons = createAsyncThunk(
  'performancePane/fetchHorizons',
  async ({ resourceId }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/data/available-horizons/', {
        params: { resource: resourceId },
      });
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch horizons');
    }
  }
);

export const fetchPaneLeague = createAsyncThunk(
  'performancePane/fetchLeague',
  async ({ resourceId, userId, year, month, horizonOffset }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/market/report/monthly-stats/list', {
        // qualification is track-independent; deterministic mirrors MonthlyPerformanceCard
        params: {
          resource: resourceId, year, month, track: 'deterministic', user: userId,
          horizon_offset: horizonOffset,
        },
      });
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message || 'Failed to fetch league status');
    }
  }
);

const initialState = {
  open: false,
  everOpened: false,
  userId: null,
  track: 'deterministic',
  horizonOffset: null,
  mode: 'rank',
  unit: 'mw',
  year: null,
  month: null,
  byResource: {},
};

const entry = (state, resourceId) => {
  if (!state.byResource[resourceId]) state.byResource[resourceId] = {};
  return state.byResource[resourceId];
};

const performancePaneSlice = createSlice({
  name: 'performancePane',
  initialState,
  reducers: {
    openPane(state, action) {
      const { userId, track, horizonOffset, year, month } = action.payload || {};
      state.open = true;
      state.everOpened = true;
      if (userId != null) state.userId = String(userId);
      if (track) state.track = track;
      if (horizonOffset !== undefined) state.horizonOffset = horizonOffset;
      if (year != null) state.year = year;
      if (month != null) state.month = month;
    },
    closePane(state) {
      state.open = false;
    },
    setPaneUser(state, action) {
      state.userId = action.payload != null ? String(action.payload) : null;
    },
    setPaneTrack(state, action) {
      state.track = action.payload;
    },
    setPaneHorizon(state, action) {
      state.horizonOffset = action.payload;
    },
    setPaneMode(state, action) {
      state.mode = action.payload;
    },
    setPaneUnit(state, action) {
      state.unit = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaneSeries.pending, (state, action) => {
        const e = entry(state, action.meta.arg.resourceId);
        e.seriesStatus = 'loading';
        // Stamp the key at pending so the component's freshness guard sees
        // an in-flight request and does not dispatch a duplicate fetch.
        e.seriesKey = action.meta.arg.fetchKey;
        e.seriesError = null;
      })
      .addCase(fetchPaneSeries.fulfilled, (state, action) => {
        const e = entry(state, action.meta.arg.resourceId);
        // Ignore a fulfillment for a key that is no longer the latest
        // dispatched request (e.g. filters changed while it was in flight).
        if (action.meta.arg.fetchKey !== e.seriesKey) return;
        e.seriesStatus = 'ok';
        e.series = action.payload;
      })
      .addCase(fetchPaneSeries.rejected, (state, action) => {
        const e = entry(state, action.meta.arg.resourceId);
        // Same staleness guard as fulfilled: an out-of-date rejection must
        // not clobber the newer in-flight request's status.
        if (action.meta.arg.fetchKey !== e.seriesKey) return;
        if (action.payload?.forbidden) {
          e.seriesStatus = 'forbidden';
        } else {
          e.seriesStatus = 'error';
          e.seriesError = action.payload;
        }
      })
      .addCase(fetchPaneHorizons.pending, (state, action) => {
        const e = entry(state, action.meta.arg.resourceId);
        e.horizonsStatus = 'loading';
      })
      .addCase(fetchPaneHorizons.fulfilled, (state, action) => {
        const e = entry(state, action.meta.arg.resourceId);
        e.horizonsStatus = 'ok';
        e.horizons = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPaneHorizons.rejected, (state, action) => {
        const e = entry(state, action.meta.arg.resourceId);
        // 'error' is settled: the union falls back to the dashboard's list for
        // this resource rather than refetching on every render.
        e.horizonsStatus = 'error';
      })
      .addCase(fetchPaneLeague.pending, (state, action) => {
        const e = entry(state, action.meta.arg.resourceId);
        e.leagueStatus = 'loading';
        // Same dedup rationale as fetchPaneSeries.pending.
        e.leagueUserId = action.meta.arg.userId != null ? String(action.meta.arg.userId) : null;
        e.leagueHorizon = action.meta.arg.horizonOffset;
      })
      .addCase(fetchPaneLeague.fulfilled, (state, action) => {
        const e = entry(state, action.meta.arg.resourceId);
        const argUserId = action.meta.arg.userId != null ? String(action.meta.arg.userId) : null;
        // Ignore a fulfillment whose user/horizon no longer match the latest
        // dispatched request for this resource.
        if (e.leagueUserId !== argUserId || e.leagueHorizon !== action.meta.arg.horizonOffset) return;
        e.leagueStatus = 'ok';
        e.league = action.payload;
      })
      .addCase(fetchPaneLeague.rejected, (state, action) => {
        const e = entry(state, action.meta.arg.resourceId);
        const argUserId = action.meta.arg.userId != null ? String(action.meta.arg.userId) : null;
        // Same staleness guard as fulfilled.
        if (e.leagueUserId !== argUserId || e.leagueHorizon !== action.meta.arg.horizonOffset) return;
        e.leagueStatus = 'error';
      });
  },
});

export const {
  openPane, closePane, setPaneUser, setPaneTrack, setPaneHorizon, setPaneMode, setPaneUnit,
} = performancePaneSlice.actions;

export const selectPane = (state) => state.performancePane;
export const selectPaneOpen = (state) => state.performancePane.open;
export const selectPaneEverOpened = (state) => state.performancePane.everOpened;
export const selectPaneByResource = (state) => state.performancePane.byResource;

export default performancePaneSlice.reducer;
